"use client";

// The elements Markdown renders. A client module, so a server component's Markdown sends references
// to these (and plain text) in its RSC payload instead of rendered Typography elements.
import type React from "react";
import BreadcrumbLink from "src/components/common/BreadcrumbLink/BreadcrumbLink";
import Link from "src/components/common/Link/Link";
import Typography from "src/components/common/Typography/Typography";

export const MarkdownHeading1 = (props: React.ComponentProps<"h1">) => (
  <Typography as="h1" variant="heading1" {...props} />
);

export const MarkdownHeading2 = (props: React.ComponentProps<"h2">) => (
  <Typography as="h2" variant="heading2" {...props} />
);

export const MarkdownHeading3 = (props: React.ComponentProps<"h3">) => (
  <Typography as="h3" variant="heading3" {...props} />
);

export const MarkdownHeading4 = (props: React.ComponentProps<"h4">) => (
  <Typography as="h4" variant="heading4" {...props} />
);

export const MarkdownHeading5 = (props: React.ComponentProps<"h5">) => (
  <Typography as="h5" variant="heading4" {...props} />
);

export const MarkdownHeading6 = (props: React.ComponentProps<"h6">) => (
  <Typography as="h6" variant="heading4" {...props} />
);

export const MarkdownParagraph = (props: React.ComponentProps<"p">) => (
  <Typography as="p" variant="body" {...props} />
);

// Code elements receive `className` (`language-*` for a fenced block, else undefined).
export const MarkdownCode = ({ className, ...props }: React.ComponentProps<"code">) => (
  <Typography as="code" variant="code" className={className} {...props} />
);

type MarkdownLinkProps = Omit<React.ComponentPropsWithoutRef<"a">, "color">;

const isInternal = (href?: string): href is string =>
  Boolean(href?.startsWith("/") && !href.startsWith("//"));

/** A plain prose link: internal paths navigate client-side, others are ordinary anchors. */
export const MarkdownLink = ({ href, target, ...props }: MarkdownLinkProps) =>
  isInternal(href) ? (
    <Link
      href={href}
      target={target === "_blank" ? "_blank" : undefined}
      variant="hyperlink"
      {...props}
    />
  ) : (
    <a href={href} target={target} {...props} />
  );

/** A docs reference: internal paths render as BreadcrumbLink (portal prefixes and icons). */
export const MarkdownReferenceLink = ({ href, target, ...props }: MarkdownLinkProps) =>
  isInternal(href) ? (
    <BreadcrumbLink href={href} target={target === "_blank" ? "_blank" : undefined} {...props} />
  ) : (
    <a href={href} target={target} {...props} />
  );
